import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
  history: { userOnly: true },
};
const READ_ONLY_MODULES = { toolbar: false };

interface TemplateEditorProps {
  id: string;
  value: string;
  onChange: (html: string) => void;
  readOnly: boolean;
}

export default function TemplateEditor({ id, value, onChange, readOnly }: TemplateEditorProps) {
  const editorRef = useRef<ReactQuill>(null);
  // Keep Quill's own HTML as its controlled value. The email serializer produces
  // inline styles, which must not be fed back into Quill on every keystroke.
  const [editorValue, setEditorValue] = useState(value);
  const emittedValue = useRef(value);

  useEffect(() => {
    if (value !== emittedValue.current) {
      setEditorValue(value);
      emittedValue.current = value;
    }
  }, [value]);

  const handleChange: NonNullable<ReactQuill.ReactQuillProps['onChange']> = (html, delta, source, editor) => {
    setEditorValue(html);
    if (source !== 'user' || readOnly) {
      return;
    }
    const converter = new QuillDeltaToHtmlConverter(editor.getContents().ops ?? [], {
      encodeHtml: true,
      paragraphTag: 'p',
      multiLineParagraph: false,
      linkTarget: '',
      inlineStyles: true,
    });
    // Quill represents an empty document as <p><br></p>; send an actual empty
    // string so required-message validation and optional messages still work.
    const serialized = editor.getText().trim() || editor.getLength() > editor.getText().length
      ? converter.convert()
      : '';
    emittedValue.current = serialized;
    onChange(serialized);
  };

  return (
    <div className="template-editor">
      {!readOnly && (
        <div className="d-flex gap-2 mb-2">
          <button type="button" onClick={() => editorRef.current?.getEditor().history.undo()}>🔙 Zpět</button>
          <button type="button" onClick={() => editorRef.current?.getEditor().history.redo()}>🔜 Opakovat</button>
        </div>
      )}
      <ReactQuill
        ref={editorRef}
        id={id}
        value={editorValue}
        onChange={handleChange}
        readOnly={readOnly}
        modules={readOnly ? READ_ONLY_MODULES : EDITOR_MODULES}
      />
    </div>
  );
}
